import { CommonModule, NgClass } from '@angular/common';
import { Component, computed, EventEmitter, HostBinding, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmErrorDirective, HlmFormFieldModule, HlmHintDirective } from '@spartan-ng/ui-formfield-helm';
import { HlmInputModule } from '@spartan-ng/ui-input-helm';
import { HlmLabelDirective } from '@spartan-ng/ui-label-helm';
import { LucideAngularModule } from 'lucide-angular';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-input',
  imports: [
    HlmInputModule,
    HlmLabelDirective,
    HlmFormFieldModule,
    NgClass,
    HlmErrorDirective,
    HlmHintDirective,
    ReactiveFormsModule,
    LucideAngularModule,
    NgxMaskDirective,
    CommonModule,
  ],
  templateUrl: './input.component.html',
  providers: [provideNgxMask()],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true, optional: true }),
    },
  ],
  standalone: true,
})
export class InputComponent implements OnInit, OnDestroy {
  @HostBinding('class') class = 'block';

  @Output() suffixClick = new EventEmitter();

  @Input() inputMode: InputMode = 'text';
  @Input() type: HTMLInputType = 'text';
  @Input() disabled: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() autocomplete: string;
  @Input() placeholder: string = '';
  @Input() suffixIcon: string;
  @Input() prefixIcon: string;
  @Input() iconSize: string;
  @Input() control: string;
  @Input() label: string;
  @Input() hint: string;
  @Input() maskType: 'phone' | null = null;

  @Output() onChange = new EventEmitter<Event>();

  private readonly parentContainer = inject(ControlContainer);

  get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  protected required = computed(() => this.formControl.hasValidator(Validators.required));

  errorMessages: Record<string, string>;

  setErrorMessages() {
    this.errorMessages = {
      required: 'This field is required',
      email: 'Invalid email format',
    };
  }

  displayErrors() {
    const { invalid, dirty, touched } = this.formControl;
    return invalid && (dirty || touched);
  }

  ngOnInit(): void {
    this.parentFormGroup.addControl(this.control, new FormControl(null), { emitEvent: false });
    this.setErrorMessages();
  }

  get formControl() {
    return this.parentFormGroup.get(this.control) as FormControl;
  }

  protected _computeMask() {
    switch (this.maskType) {
      case 'phone':
        return '+000 000-00-000';
      default:
        return undefined;
    }
  }

  ngOnDestroy(): void {
    this.parentFormGroup.removeControl(this.control);
  }
}

type InputMode = 'numeric' | 'email' | 'text' | 'decimal' | 'tel' | 'search' | 'url';

type HTMLInputType =
  | 'button'
  | 'checkbox'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'hidden'
  | 'image'
  | 'month'
  | 'number'
  | 'password'
  | 'radio'
  | 'range'
  | 'reset'
  | 'search'
  | 'submit'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';
